import React, { createContext, useContext, useMemo } from 'react';
import type { CSSProperties, PropsWithChildren } from 'react';
import type {
  DraggableSyntheticListeners,
  UniqueIdentifier,
} from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Paper as MuiPaper } from '@mui/material';
import { styled as muiStyled } from '@mui/material/styles';
import { DragIndicator } from '@mui/icons-material';

import './SortableItem.css';

const Paper = muiStyled(MuiPaper)(({}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 20,
  marginBottom: 16,
  minHeight: 210,
  padding: 24,
  border: '1px solid #e2e8f0',
  borderRadius: 12,
  boxShadow:
    '0 1px 3px rgba(15, 23, 42, 0.05), 0 1px 2px rgba(15, 23, 42, 0.04)',
  transition: 'box-shadow 160ms ease, border-color 160ms ease',
  '&:hover': {
    borderColor: '#cbd5e1',
    boxShadow: '0 8px 18px rgba(15, 23, 42, 0.08)',
  },
  '@media (max-width: 1023px)': {
    flexWrap: 'wrap',
    padding: 16,
  },
}));

interface Props {
  id: UniqueIdentifier;
}

interface Context {
  attributes: Record<string, any>;
  listeners: DraggableSyntheticListeners;
  ref(node: HTMLElement | null): void;
}

const SortableItemContext = createContext<Context>({
  attributes: {},
  listeners: undefined,
  ref() {},
});

export function SortableItem({ children, id }: PropsWithChildren<Props>) {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({ id });
  const context = useMemo(
    () => ({
      attributes,
      listeners,
      ref: setActivatorNodeRef,
    }),
    [attributes, listeners, setActivatorNodeRef],
  );
  const style: CSSProperties = {
    opacity: isDragging ? 0.4 : undefined,
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <SortableItemContext.Provider value={context}>
      <Paper
        ref={setNodeRef}
        style={style}
        elevation={1}
        sx={{ width: '100%' }}
      >
        {children}
      </Paper>
    </SortableItemContext.Provider>
  );
}

export function DragHandle() {
  const { attributes, listeners, ref } = useContext(SortableItemContext);
  if (ref) {
    return (
      <button className="DragHandle" {...attributes} {...listeners} ref={ref}>
        <DragIndicator />
      </button>
    );
  }
  return null;
}
